import { isNull, isNumber, isString } from 'lodash';
import numbro from 'numbro';
import { Currency } from '../constants';

export const num = (input: number | string) => numbro(input);

export const add = (a: number, b: number) => numbro(a).add(b).value();

export const subtract = (a: number, b: number) => numbro(a).subtract(b).value();

export const multiply = (a: number, b: number) => numbro(a).multiply(b).value();

export const divide = (a: number, b: number) => numbro(a).divide(b).value();

export const sum = (...args: number[]) => {
    const result = numbro(0);
    args.forEach((num) => result.add(num));
    return result.value();
};

export const sumBy = <T>(array: readonly T[], iteratee: (item: T) => number) => {
    let result: numbro.Numbro | undefined;

    array.forEach((item) => {
        const value = iteratee(item);

        if (result === undefined) {
            result = numbro(value);
        } else {
            result.add(value);
        }
    });

    return result?.value() ?? 0;
};

export type FormatNumberOptions = {
    decimalPlaces?: number;
    short?: boolean;
    prefix?: string;
    postfix?: string;
    percent?: boolean;
    trimZeros?: boolean;
    forceAverage?: numbro.Format['forceAverage'];
    thousandSeparated?: boolean;
    rounding?: boolean | 'up' | 'down';
    rtl?: boolean; // to be used only when cannot use <bdi>
};

export const formatNumber = (input?: number | string | null, options: FormatNumberOptions = {}): string => {
    if (input === null || isNaN(Number(input))) {
        return '-';
    }

    const {
        decimalPlaces = 2,
        short = false,
        prefix = '',
        postfix = '',
        percent = false,
        trimZeros = false,
        forceAverage = undefined,
        thousandSeparated = true,
        rounding = true,
        rtl = false,
    } = options;

    const formatOptions: numbro.Format = {
        prefix,
        postfix,
        mantissa: decimalPlaces,
        trimMantissa: trimZeros,
        average: short,
        thousandSeparated,
        output: percent ? 'percent' : 'number',
        currencyPosition: 'prefix',
    };

    // IMPORTANT: We need to manually specify forceAverage due to numbro's threshold handling.
    // Without explicit settings, numbro may choose to format 572,600,000 as "0.57b" (billion) instead of the expected "572.60M" (million).
    if (short) {
        const value = Math.abs(Number(input));

        if (value >= 1e12) {
            formatOptions.forceAverage = 'trillion';
        } else if (value >= 1e9) {
            formatOptions.forceAverage = 'billion';
        } else if (value >= 1e6) {
            formatOptions.forceAverage = 'million';
        } else if (value >= 1e3) {
            formatOptions.forceAverage = 'thousand';
        } else {
            formatOptions.average = false;
        }
    } else if (forceAverage) {
        formatOptions.average = true;
        formatOptions.forceAverage = forceAverage;
    }

    if (rounding === 'up') {
        formatOptions.roundingFunction = Math.ceil;
    } else if (rounding === 'down' || rounding === false) {
        formatOptions.roundingFunction = Math.floor;
    }

    if (Number(input) < 0 && rtl) {
        return `${numbro(Math.abs(Number(input)))
            .format(formatOptions)
            .toUpperCase()}-`;
    }

    return numbro(input).format(formatOptions).toUpperCase();
};

export type FormatMoneyOptions = {
    decimalPlaces?: number;
    short?: boolean;
    aliasForZero?: boolean | string;
    trimZeros?: boolean;
    localeCode?: string;
    currency?: string | null;
};

export const formatMoney = (input?: number | string, options: FormatMoneyOptions = {}): string => {
    if (isNaN(Number(input))) {
        return '-';
    }

    const {
        decimalPlaces = 2,
        short = false,
        aliasForZero = false,
        trimZeros = false,
        localeCode = 'en-US',
        currency = Currency.UsDollar,
    } = options;

    const validatedCurrency = isNull(currency) ? Currency.UsDollar : currency;

    const formatter = new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency: validatedCurrency,
        minimumFractionDigits: trimZeros ? 0 : decimalPlaces < 2 ? decimalPlaces : 2,
        maximumFractionDigits: trimZeros ? 0 : decimalPlaces,
        notation: short ? 'compact' : undefined,
        // @ts-expect-error - missing from types
        roundingMode: 'floor',
    });

    const number = formatter.format(Number(input));

    if (aliasForZero) {
        if (isNumber(input) && input === 0) {
            return isString(aliasForZero) ? aliasForZero : 'N/A';
        }
        return number;
    } else {
        return number;
    }
};

export type FormatPercentageOptions = {
    decimalPlaces?: number;
    withConversion?: boolean;
    withChange?: boolean;
    withSymbol?: boolean;
    trimZeros?: boolean;
    rtl?: boolean;
};

export const formatPercentage = (input?: number | string | null, options: FormatPercentageOptions = {}): string => {
    const {
        decimalPlaces = 2,
        withConversion = false,
        withChange = false,
        withSymbol = true,
        trimZeros = false,
        rtl = false, // to be used only when cannot use <bdi>
    } = options;

    let result = formatNumber(input, {
        decimalPlaces,
        postfix: withConversion ? undefined : '%',
        percent: withConversion,
        trimZeros: trimZeros,
        rtl,
    });

    result = `${(withChange && (Number(input) > 0 ? '' : '-')) || ''}${result}`;
    return withSymbol ? result : result.substring(0, result.length - 1);
};

export const validateRoutingNumber = (routingNumber: string): boolean => {
    // Check if it's 9 digits
    const regex = /^\d{9}$/;
    if (!regex.test(routingNumber)) {
        return false;
    }

    // Validate the checksum
    const digits = routingNumber.split('').map(Number);

    const checksum =
        ((digits[0] || 0) + (digits[3] || 0) + (digits[6] || 0)) * 3 +
        ((digits[1] || 0) + (digits[4] || 0) + (digits[7] || 0)) * 7 +
        ((digits[2] || 0) + (digits[5] || 0) + (digits[8] || 0)) * 1;

    return checksum % 10 === 0;
};

export const validateAccountNumber = (accountNumber: string): boolean => {
    // Check if it's a number and within a reasonable length (typically between 4 and 17 digits)
    const regex = /^\d{4,17}$/;
    return regex.test(accountNumber);
};

export const validateBankDetails = (
    routingNumber: string,
    accountNumber: string,
): { isRoutingValid: boolean; isAccountValid: boolean } => {
    const isRoutingValid = validateRoutingNumber(routingNumber);
    const isAccountValid = validateAccountNumber(accountNumber);
    return {
        isRoutingValid,
        isAccountValid,
    };
};

export const getCurrencySymbol = (currencyCode: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
    });

    const parts = formatter.formatToParts(1);
    return parts.find((part) => part.type === 'currency')?.value ?? '$';
};
